import { fromZonedTime } from 'date-fns-tz';
import Holidays from 'date-holidays';
import { BA_TIMEZONE } from './timezone';

/**
 * Feriados nacionales de Argentina (Ley 27.399: fijos + trasladables a lunes + móviles de
 * Pascua/Carnaval), calculados dinámicamente para cualquier año por date-holidays — no es una
 * lista de fechas hardcodeada, así que no hay que actualizarla año a año.
 */
const argentinaHolidays = new Holidays('AR');

interface CivilDate {
  year: number;
  month: number; // 1-12
  day: number;
}

const pad = (n: number) => String(n).padStart(2, '0');

function toCivilDate(date: Date): CivilDate {
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}

function fromCivilDate(civil: CivilDate): Date {
  return new Date(civil.year, civil.month - 1, civil.day);
}

/**
 * Día de la semana de una fecha calendario (0=domingo..6=sábado), calculado con aritmética
 * Gregoriana pura vía Date.UTC usado como calculadora de calendario — no representa un instante
 * real, así que no depende de la zona horaria del navegador/servidor donde corre el código.
 */
function civilWeekday(civil: CivilDate): number {
  return new Date(Date.UTC(civil.year, civil.month - 1, civil.day)).getUTCDay();
}

function isCivilWeekend(civil: CivilDate): boolean {
  const weekday = civilWeekday(civil);
  return weekday === 0 || weekday === 6;
}

function addCivilDays(civil: CivilDate, days: number): CivilDate {
  const d = new Date(Date.UTC(civil.year, civil.month - 1, civil.day + days));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/**
 * Chequea si una fecha calendario es feriado nacional en Argentina. Cuenta solo el tipo
 * 'public' de date-holidays (fijos + trasladables a lunes + móviles de Pascua/Carnaval) —
 * se excluyen 'bank' (ej. Jueves Santo, feriado bancario, no nacional) y 'optional' (días no
 * laborables con fines turísticos, no garantizados año a año).
 *
 * La fecha se ancla explícitamente a medianoche en horario argentino (vía fromZonedTime) antes
 * de consultar la librería: date-holidays calcula sus ventanas de feriado en la zona horaria del
 * país (Argentina), y comparar contra un Date construido en la zona horaria local del proceso
 * (navegador o servidor, que puede no ser Argentina) da resultados incorrectos cerca de la
 * medianoche — se confirmó este desfasaje con un feriado real (Carnaval) antes de esta corrección.
 */
function isArgentineNationalHoliday(civil: CivilDate): boolean {
  const instant = fromZonedTime(`${civil.year}-${pad(civil.month)}-${pad(civil.day)}T00:00:00`, BA_TIMEZONE);
  const matches = argentinaHolidays.isHoliday(instant);
  if (!matches) return false;
  return matches.some((h) => h.type === 'public');
}

function isBusinessDay(civil: CivilDate): boolean {
  return !isCivilWeekend(civil) && !isArgentineNationalHoliday(civil);
}

/**
 * Fecha límite de pago de un mes cerrado: el día 5 calendario del mes siguiente; si ese día 5
 * cae en fin de semana o feriado nacional argentino, la fecha límite pasa al próximo día hábil.
 */
export function calculatePaymentDueDate(closedMonth: Date): Date {
  const closed = toCivilDate(closedMonth);
  let candidate: CivilDate =
    closed.month === 12
      ? { year: closed.year + 1, month: 1, day: 5 }
      : { year: closed.year, month: closed.month + 1, day: 5 };

  while (!isBusinessDay(candidate)) {
    candidate = addCivilDays(candidate, 1);
  }

  return fromCivilDate(candidate);
}

export type SettlementStatus = 'pending' | 'paid';

function toDateOnly(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * 'pending' si hoy es anterior a la fecha límite, 'paid' si es igual o posterior — sin verificar
 * acreditación real del pago, es una simplificación aceptada para V1.
 */
export function getSettlementStatus(dueDate: Date, today: Date = new Date()): SettlementStatus {
  return toDateOnly(today).getTime() < toDateOnly(dueDate).getTime() ? 'pending' : 'paid';
}
